// Staff invitations view - playtester invitation management via d3-auth

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';

class StaffInvitationsView extends BaseComponent {
  static properties = {
    _invitations: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
    _email: { type: String, state: true },
    _message: { type: String, state: true },
    _creating: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    this._invitations = [];
    this._loading = true;
    /** @type {string|null} */
    this._error = null;
    this._email = '';
    this._message = '';
    this._creating = false;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._loadInvitations();
  }

  async _loadInvitations() {
    this._loading = true;
    this._error = null;

    try {
      const data = await api.get('/api/staff/invitations');
      this._invitations = data.invitations || [];
    } catch (err) {
      console.error('Failed to load invitations:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to load invitations';
    } finally {
      this._loading = false;
    }
  }

  async _createInvitation() {
    if (!this._email) return;
    this._creating = true;
    this._error = null;

    try {
      await api.post('/api/staff/invitations', {
        email: this._email,
        message: this._message,
      });
      this._email = '';
      this._message = '';
      await this._loadInvitations();
    } catch (err) {
      console.error('Failed to create invitation:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to create invitation';
    } finally {
      this._creating = false;
    }
  }

  /** @param {string} id */
  async _revokeInvitation(id) {
    this._error = null;
    try {
      await api.delete(`/api/staff/invitations/${id}`);
      await this._loadInvitations();
    } catch (err) {
      console.error('Failed to revoke invitation:', err);
      this._error = /** @type {Error} */ (err).message || 'Failed to revoke invitation';
    }
  }

  /** @param {string|undefined} dateString */
  _formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /** @param {string} status */
  _statusBadgeClass(status) {
    if (status === 'accepted') return 'bg-success';
    if (status === 'revoked') return 'bg-danger';
    return 'bg-warning text-dark';
  }

  render() {
    const pending = this._invitations.filter((i) => i.status === 'pending');
    const past = this._invitations.filter((i) => i.status !== 'pending');

    return html`
      ${this._error ? html`<div class="alert alert-danger" role="alert">${this._error}</div>` : ''}

      <div class="card mb-4">
        <div class="card-header"><h5 class="mb-0">Invite Playtester</h5></div>
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-5">
              <label class="form-label">Email</label>
              <input
                type="email"
                class="form-control"
                placeholder="playtester@example.com"
                .value="${this._email}"
                @input="${(/** @type {InputEvent} */ e) => {
                  this._email = /** @type {HTMLInputElement} */ (e.target).value;
                }}"
              />
            </div>
            <div class="col-md-5">
              <label class="form-label">Message (optional)</label>
              <input
                type="text"
                class="form-control"
                placeholder="Personal note to include in the invitation email"
                .value="${this._message}"
                @input="${(/** @type {InputEvent} */ e) => {
                  this._message = /** @type {HTMLInputElement} */ (e.target).value;
                }}"
              />
            </div>
            <div class="col-md-2">
              <button
                class="btn btn-primary w-100"
                ?disabled="${this._creating || !this._email}"
                @click="${this._createInvitation}"
              >
                ${this._creating ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="card mb-4">
        <div class="card-header"><h5 class="mb-0">Pending Invitations</h5></div>
        <div class="card-body">
          ${
            this._loading
              ? html`<div class="text-center py-3"><div class="spinner-border spinner-border-sm" role="status"></div></div>`
              : pending.length === 0
                ? html`<p class="text-muted mb-0">No pending invitations.</p>`
                : html`
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Sent</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        ${pending.map(
                          (inv) => html`
                          <tr>
                            <td>${inv.email}</td>
                            <td>${this._formatDate(inv.created_at)}</td>
                            <td class="text-end">
                              <button
                                class="btn btn-sm btn-outline-danger"
                                @click="${() => this._revokeInvitation(inv.id)}"
                              >Revoke</button>
                            </td>
                          </tr>
                        `,
                        )}
                      </tbody>
                    </table>
                  </div>
                `
          }
        </div>
      </div>

      ${
        past.length > 0
          ? html`
        <div class="card">
          <div class="card-header"><h5 class="mb-0">Past Invitations</h5></div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Sent</th>
                    <th>Responded</th>
                  </tr>
                </thead>
                <tbody>
                  ${past.map(
                    (inv) => html`
                    <tr>
                      <td>${inv.email}</td>
                      <td><span class="badge ${this._statusBadgeClass(inv.status)}">${inv.status}</span></td>
                      <td>${this._formatDate(inv.created_at)}</td>
                      <td>${this._formatDate(inv.accepted_at)}</td>
                    </tr>
                  `,
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `
          : ''
      }
    `;
  }
}

customElements.define('staff-invitations-view', StaffInvitationsView);
